import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import debouncePromise from 'debounce-promise';
import { ModalWithForm } from '../modal';
import { useShowToast } from '@/hooks';
import { AlreadyPartOf } from './components';
import { resolveSelectedContactIds } from './resolveSelectedContactIds';
import { useImportContactsMutation } from '@/store/query/contacts-list';
import { atsApplicationApi } from '@/store/query/ats-jobs';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import {
  SelectOptionWithAvatar,
  SelectWithDescriptionOption,
  CandidateSourceMenu,
  CandidateSourceSingleValue,
} from '@/components/formBuilder/SelectComponents';
import { useGetCandidateSourcesQuery } from '@/store/query/candidate-sources';
import { JobListMenu } from '../filters/components/UiItems/JobList';
import { useBulkOperation } from '@/components/ats/BulkOperationQueue';
import { BulkError } from '../BulkError';
import { Flex, useDisclosure } from '@chakra-ui/react';
import { AutomationBadge } from '@/components/automation/AutomationBadge';
import { KText } from '@/ui-library/components/text';
import { FormNotes } from '@/components/formBuilder/components/FormNotes';
import { useGetContactByIdQuery } from '@/store/query/contacts';
import { useKFlags } from '@/hooks/use-k-flags';
import {
  AutomationPreviewField,
  useStageAutomationRule,
  useJobAutomatedStageIds,
  MOVE_WITH_AUTOMATION_STRINGS,
} from '@/components/automation/MoveWithAutomationModal';
import {
  useRegisterDispatchSession,
  useEnqueueAddToJobAutomation,
} from '@/components/automation/UnderwayPopup';

const errorMap = {
  application_exists: 'Contact already present in the job',
};

// AsyncSelect provides no static `options` prop, so JobListMenu would read `undefined`
// until the first loadOptions resolves. Default it to [] here (scoped to this picker)
// rather than changing the shared JobListMenu. Module-scoped so its identity is stable.
const AddJobListMenu = (menuProps: any) => (
  <JobListMenu
    {...menuProps}
    selectProps={{
      ...menuProps.selectProps,
      options: menuProps.selectProps?.options || [],
    }}
  />
);

export const AddToJob = ({
  modalActions,
  candidateId,
  isBulk = false,
  onSuccess = () => {},
  alreadyPartOfProps = {},
  existingJobs = [],
  enableStageOption = false,
  triggerRef = 'project',
  bulkOpMethods = null,
  selected = [], // Selected contacts with full data
  enableBulkActivity = false, // Only true from Jobs and Contacts pages
  enableApi = false,
}) => {
  const [errorContacts, setErrorContacts] = useState(null);
  // Bulk operation tracking
  const internalBulkOp = useBulkOperation({
    activityType: 'add_to_job',
  });
  const selectedContact = Array.isArray(selected)
    ? selected.length === 1
      ? selected[0]
      : {}
    : selected || {};
  const { ats_candidate_source = {}, credited_to = {} } = selectedContact;

  //for flows & projects
  const { data: contactData } = useGetContactByIdQuery(
    {
      contactId: candidateId.length > 1 ? candidateId[0] : candidateId,
    },
    { skip: !enableApi }
  );

  const errorsActions = useDisclosure();
  // Use external bulkOpMethods if provided, otherwise use internal
  const { startTracking, completeSuccess, completeWithErrors, completeFailed } =
    bulkOpMethods || internalBulkOp;

  // Get contact IDs for the action. `selected` may be raw contacts, applications
  // with a nested contact, or react-table rows ({ original }) from Flows/Projects
  // bulk actions — resolveSelectedContactIds handles all shapes. See its doc.
  const targetIds = useMemo(() => {
    if (isBulk && selected?.length > 0) {
      return resolveSelectedContactIds(selected);
    }
    return Array.isArray(candidateId)
      ? candidateId
      : [candidateId].filter(Boolean);
  }, [isBulk, selected, candidateId]);
  const [stageOptions, setStageOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const formRef = useRef(null);
  const { feStageAutomation } = useKFlags();
  // Registers the bulk add-to-job `dispatch_session_id` so the global
  // DispatchSessionWatcher polls the automation to readiness and shows the
  // underway popup — same mechanism the bulk stage-move uses.
  const registerDispatchSession = useRegisterDispatchSession();
  // Non-bulk (sync) add path: the response is the created-applications array,
  // whose pending executions are created inline — open the underway popup by
  // looking them up (mirrors ImportCandidateToJob / CreateContact).
  const enqueueAddToJobAutomation = useEnqueueAddToJobAutomation();
  // Picked stage — drives the target-stage automation preview.
  const [selectedStageId, setSelectedStageId] = useState(null);
  // Selected job — drives the ⚡ markers for its automated stages in the
  // stage dropdown (the option stages carry no automation field).
  const [selectedJobId, setSelectedJobId] = useState(null);
  // Lazy trigger so the job dropdown loads options straight from the server per query
  // (see fetchJobOptions) instead of eagerly fetching a capped first page. Same ENG-3338
  // fix as TransferToJob: the old `limit: 500` page dropped jobs ranked >500 on large
  // accounts, so a valid job was unreachable in the "Add to job" picker.
  const [triggerJobSearch] = atsApplicationApi.useLazyGetAllJobsQuery();
  const { data: candidateSources } = useGetCandidateSourcesQuery(
    {},
    { skip: isLoading }
  );
  const availableSources = candidateSources?.filter(
    (item) => item?.enabled && !item?.deleted
  );
  const toast = useShowToast();
  const currUser = useSelector(selectCurrUser);
  const users = useSelector(selectUsers);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const usersOptions = users?.map((user) => ({
    ...user,
    label: user?.name,
    value: user?.id,
  }));

  const existingJobIds = useMemo(
    () => existingJobs?.map((job) => job?.job_id) || [],
    [existingJobs]
  );

  // Map a raw job into the picker option shape (mirrors the old inline map).
  const mapJobToOption = useCallback(
    (item) => ({
      value: item?.id,
      label: item?.name,
      status: item?.state,
      department: item?.department,
      locations: item?.offices || [],
      ...item,
    }),
    []
  );

  // loadOptions always queries the server (empty q -> base list on open, q text -> DB
  // search) so any published/scheduled job is reachable regardless of the total count.
  const fetchJobOptions = useCallback(
    async (input) => {
      try {
        const result = await triggerJobSearch({
          params: {
            view: 'interview_plan',
            scope: 'viewable',
            filter_by: { job_statuses: ['published', 'scheduled'] },
            sort_by: { open_at: 'DESC' },
            limit: 50,
            q: input || '',
          },
        }).unwrap();
        return (result || [])
          .filter((item) => !existingJobIds.includes(item?.id))
          .map(mapJobToOption);
      } catch {
        return [];
      }
    },
    [triggerJobSearch, existingJobIds, mapJobToOption]
  );

  // Debounce so typing doesn't fire a request per keystroke.
  const debouncedLoadJobOptions = useMemo(
    () => debouncePromise(fetchJobOptions, 300),
    [fetchJobOptions]
  );

  const switchLabelStyles = {
    size: 'ats-sm-regular',
    marginBottom: '0',
    cursor: 'pointer',
  };

  const labelStyles = {
    size: 'ats-sm-bold',
  };

  // ── Target stage automation preview ────────────────────────────────
  // Adding to a job runs the target stage's workflow_rule. The candidate
  // keeps their current job, so nothing is stopped — only the target side
  // is previewed. Track the picked job/stage so the rule can be fetched.
  const handleFormObserve = useCallback((values) => {
    const nextStage = values?.stage_id ?? null;
    setSelectedStageId((prev) =>
      String(prev ?? '') === String(nextStage ?? '') ? prev : nextStage
    );
  }, []);

  // The picked job's stages live in `stageOptions` (set in the job field's
  // customOnChange), so the preview keeps working with the lazy/server-side
  // job search (ENG-3338) — no full ats_jobs list needed.
  // When the stage is user-selectable use the picked stage; otherwise the
  // add defaults to Application Review (pos 2) or the first stage — mirror
  // that here so the preview reflects where the candidate will land.
  const previewStageId = enableStageOption
    ? selectedStageId
    : (stageOptions?.find(
        (s) => s?.name === 'Application Review' && s?.position === 2
      )?.id ?? stageOptions?.[0]?.id);
  const { data: targetRule } = useStageAutomationRule(previewStageId, {
    enabled: feStageAutomation,
  });
  // Stage ids of the picked job that have automation — for the ⚡ markers on
  // the stage dropdown options.
  const { data: automatedStageIds } = useJobAutomatedStageIds(selectedJobId, {
    enabled: feStageAutomation,
  });
  const targetStageName =
    stageOptions?.find((s) => String(s?.id) === String(previewStageId))?.name ??
    MOVE_WITH_AUTOMATION_STRINGS.fallback.stageName;
  const showAutomationPreview = Boolean(
    feStageAutomation && (targetRule?.actions?.length ?? 0) > 0
  );

  const formConfig = [
    ...(triggerRef === 'job'
      ? [
          {
            name: 'note_banner',
            type: 'custom',
            Field: () => (
              <FormNotes
                noteValue="The candidate will remain in this job and also be added to the new one"
                props={{
                  mt: '12px',
                  p: '8px 2px',
                }}
              />
            ),
          },
        ]
      : []),
    {
      name: 'job_id',
      label: 'Select job ',
      type: 'autoComplete',
      placeholder: 'Select job',
      labelStyles: labelStyles,
      valueKey: 'value',
      labelKey: 'label',
      isRequired: true,
      // Portal the menu to <body> so it overlays the modal instead of opening
      // inside the capped scrollable body (which triggers the body scroll).
      menuPortalTarget: document.body,
      // AsyncSelect: base list on open (empty q), DB search-by-`q` on type. JobListMenu
      // still renders the rich rows; filterOption is disabled so the menu owns display
      // filtering.
      loadOptions: debouncedLoadJobOptions,
      cacheOptions: true,
      filterOption: () => true,
      components: {
        Menu: AddJobListMenu,
        IndicatorSeparator: () => null,
      },
      customOnChange: (value) => {
        setStageOptions(value?.stages || []);
        setSelectedJobId(value?.value ?? null);
        formRef.current?.setFieldValue('stage_id', null, false);
        formRef.current?.setFieldTouched('stage_id', false, false);
      },
    },
    {
      name: 'stage_id',
      label: 'Select stage',
      placeholder: 'Select stage',
      type: 'kSelect',
      isRequired: true,
      disabled: stageOptions.length === 0,
      labelStyles: labelStyles,
      options: stageOptions,
      valueKey: 'id',
      labelKey: 'name',
      // Portal the menu to <body> (fixed) so it overlays the modal instead of
      // opening inside the now-capped scrollable body (which would push the
      // content down and trigger the body scroll). Matches the source field.
      menuPortalTarget: document.body,
      menuPosition: 'fixed',
      // ⚡ on the RIGHT end of stages that have automation configured, so the
      // user sees which target stage will run automation on the added
      // candidate(s). Name takes the free space (flex 1) so the badge is pushed
      // to the far end — mirrors the shared `renderStageOptionLabel`.
      customFormatLabel: (option, key) => (
        <Flex alignItems="center" gap="6px" w="100%" minW={0}>
          <KText
            size="sm-regular"
            color="secondary-900"
            flex="1"
            minW={0}
            isTruncated
          >
            {option?.[key]}
          </KText>
          {automatedStageIds?.has(String(option?.id)) && (
            <AutomationBadge ml="auto" />
          )}
        </Flex>
      ),
    },
    {
      // Automation preview sits right under the stage picker (above the
      // source/credit toggle) — a custom field so it slots between form
      // fields rather than below the whole form. Renders nothing when the
      // stage has no automation.
      type: 'custom',
      name: 'automation_preview',
      Field: AutomationPreviewField,
      props: {
        show: showAutomationPreview,
        containerProps: { my: '16px' },
        targetStageName,
        targetRule,
        // Let the activity list grow with the modal body (single scroll)
        // rather than showing its own inner scrollbar.
        scrollableActivities: false,
        dataTestId: `automation-preview-add-${isBulk ? 'bulk' : 'single'}`,
      },
    },
    {
      name: 'source_credits',
      label: 'Change source and credited to',
      type: 'switch',
      labelStyles: switchLabelStyles,
      initialValue: currUser.id,
      style: {
        flexDirection: 'row-reverse',
        justifyContent: 'flex-end',
        gap: '8px',
      },
    },
    {
      isConditional: true,
      conditionConfig: { field: 'source_credits', value: true },
      name: 'ats_candidate_source_id',
      label: 'Select source',
      placeholder: 'Select source',
      type: 'kSelect',
      labelStyles: labelStyles,
      options: availableSources,
      valueKey: 'id',
      isMulti: false,
      labelKey: 'name',
      menuPortalTarget: document.body,
      menuPosition: 'fixed',
      components: {
        Menu: CandidateSourceMenu,
        SingleValue: CandidateSourceSingleValue,
        IndicatorSeparator: () => null,
      },
      styles: {
        menu: (base) => ({
          ...base,
          padding: 0,
        }),
        menuList: (base) => ({
          ...base,
          padding: 0,
        }),
      },
    },
    {
      isConditional: true,
      conditionConfig: { field: 'source_credits', value: true },
      name: 'credited_to_user_id',
      label: 'Select credited to',
      placeholder: 'Select credited to',
      type: 'kSelect',
      labelStyles: labelStyles,
      isMulti: false,
      width: '100%',
      customFormatLabel: SelectOptionWithAvatar,
      components: {
        Option: SelectWithDescriptionOption,
      },
      showAvatar: true,
      descKey: 'email',
      avatarKey: 'avatar',
      isNearLabel: true,
      options: usersOptions,
      valueKey: 'id',
    },
  ];

  const [importContact, { isLoading: isImportLoading }] =
    useImportContactsMutation();

  const handleSubmit = async (values) => {
    const { job_id, stage_id } = values;
    // stageOptions holds the selected job's stages (set in the job field's customOnChange),
    // so we no longer need the full ats_jobs list to resolve the default stage.
    const selectedStageId =
      stageOptions?.find(
        (stage) => stage?.name === 'Application Review' && stage?.position === 2
      )?.id || stageOptions?.[0]?.id;

    // Use all selected contacts (validation removed - server will handle duplicates)
    const contactsToAdd = isBulk
      ? targetIds
      : Array.isArray(candidateId)
        ? candidateId
        : [candidateId];
    const totalContacts = contactsToAdd.length;

    // Start tracking only when bulk activity is enabled (Jobs & Contacts pages)
    if (enableBulkActivity && startTracking) {
      startTracking(totalContacts);
      modalActions?.onClose(); // Close modal immediately
    }

    const payload = {
      ats_job_id: job_id,
      ats_job_stage_id: enableStageOption ? stage_id : selectedStageId,
      contact_ids: contactsToAdd,
      ats_candidate_source_id: values?.ats_candidate_source_id,
      credited_to_user_id: values?.source_credits
        ? values?.credited_to_user_id
        : null,
      ...(enableBulkActivity && { is_bulk_activity: true }),
    };

    try {
      const result: any = await importContact({ payload });
      // Handle both 'error' and 'errors' keys (API may return either)
      const errorList = result?.error || result?.errors || [];

      if (errorList?.length > 0) {
        if (enableBulkActivity) {
          // Jobs & Contacts: use BulkOperationQueue tracking
          if (completeWithErrors) {
            completeWithErrors({
              successCount: totalContacts - errorList.length,
              failedCount: errorList.length,
              errors: errorList.map((e) => ({
                id: e.id || e.contact_id || 'unknown',
                message: e.reason || e.message || 'Failed to add to job',
              })),
            });
          } else if (completeSuccess) {
            completeSuccess(totalContacts);
          }
          onSuccess?.();
        } else if (isBulk) {
          // Projects, MCO, etc.: show BulkError modal
          setErrorContacts(errorList);
          errorsActions?.onOpen();
          onSuccess?.();
        } else {
          // Single candidate: show toast
          const errorMsg =
            errorMap[errorList?.[0]?.reason] ||
            typeof errorList?.[0]?.reason === 'string'
              ? errorList?.[0]?.reason.includes(':')
                ? errorList?.[0]?.reason.split(':')[1]
                : errorList?.[0]?.reason
              : '';
          toast({
            title: `Unable to add candidate`,
            description: errorMsg,
            status: 'error',
          });
        }
      } else {
        if (enableBulkActivity) {
          // Jobs & Contacts: complete via BulkOperationQueue
          if (completeSuccess) {
            completeSuccess(totalContacts);
          }
          // Start the stage-automation underway popup. The bulk-activity add
          // response carries `dispatch_session_id`; the global watcher polls it
          // to readiness. No-ops on a blank id (automation didn't run) or when
          // the flag is off.
          if (feStageAutomation) {
            registerDispatchSession(
              (result?.data ?? result)?.dispatch_session_id
            );
          }
        } else {
          toast({ title: `Contact added to Job` });
          modalActions?.onClose();
          // Sync add: response is the created-applications array. Open the
          // underway popup for the target stage's automation (no-ops when none).
          if (feStageAutomation) {
            const responseData = result?.data ?? result;
            const applicationIds = Array.isArray(responseData)
              ? responseData.map((app) => app?.id)
              : [];
            await enqueueAddToJobAutomation(applicationIds);
          }
        }
        onSuccess?.();
      }
    } catch {
      if (enableBulkActivity && completeFailed) {
        completeFailed('Failed to add contacts to job');
      } else {
        toast({
          title: 'Unable to add candidate',
          status: 'error',
        });
      }
    }
  };

  return (
    <>
      <ModalWithForm
        isOpen={modalActions?.isOpen}
        onClose={modalActions?.onClose}
        formConfig={formConfig}
        primaryCtaText="Add to job"
        isCtaLoading={isImportLoading}
        headerText={
          triggerRef === 'job' ? 'Add to additional job' : 'Add to job'
        }
        subHeaderText={
          triggerRef === 'job' ? '' : 'Select a job to add all the candidates'
        }
        enableBorder={true}
        handleSubmit={handleSubmit}
        formContainerStyles={{ minHeight: '250px' }}
        // Cap the body so a tall form + inline automation preview scrolls as a
        // single scroll here (the automation list itself no longer scrolls —
        // see scrollableActivities={false} below), instead of growing the modal
        // past the viewport. Header + footer stay put.
        modalBodyProps={{
          py: '12px',
          maxH: '60vh',
          overflowY: 'auto',
        }}
        showAdditionalBlockAfter={true}
        AdditionalBlockAfter={AlreadyPartOf}
        additionalBlockAfterProps={alreadyPartOfProps}
        customFormRef={formRef}
        initialValues={{
          credited_to_user_id: enableApi
            ? contactData?.credited_to?.id
            : credited_to?.id,
          ats_candidate_source_id: availableSources?.find(
            (source) =>
              source?.id ===
              (enableApi
                ? contactData?.ats_candidate_source?.id
                : ats_candidate_source?.id)
          )?.id,
        }}
        // Observe job/stage changes so the destination stage's automation
        // can be fetched and previewed inline.
        isObserveEnabled={feStageAutomation}
        observFunc={handleFormObserve}
      />
      {errorsActions?.isOpen && (
        <BulkError
          total={candidateId?.length}
          contacts={errorContacts}
          modalActions={errorsActions}
          mainText={`Candidate action${
            errorContacts?.length > 1 ? 's' : ''
          } failed`}
          successMessage="successfully added to job"
          onActionSubmit={(close) => {
            close();
          }}
          mainSubText={
            errorContacts?.length > 1
              ? `These candidates are already present in the specified job, so this action is not completed.`
              : `This candidate is already present in the specified job, so this action is not completed.`
          }
        />
      )}
    </>
  );
};
